import { createFileRoute, Link } from '@tanstack/react-router'
import Breadcrumbs from '../components/Breadcrumbs'
import CollapsibleSection from '../components/CollapsibleSection'
import { FaSyncAlt } from 'react-icons/fa'
import { FiCheckCircle, FiShield } from 'react-icons/fi'
import { Sparkles, ListChecks, Grid3x3, Clock, BookOpen, HelpCircle, type LucideIcon } from 'lucide-react'
import { getBreaches, useTriggerBreaches } from '@/hooks/useBreaches'
import type { Breach } from '@/hooks/useBreaches'
import { getUserFromClerkId } from '@/hooks/useUser'
import React from 'react'
import { getArticles } from '@/hooks/useArticle'
import ArticleCard from '@/components/ArticleCard'
import BreachExplainer from '@/components/BreachExplainer'
import gLogoViolet from '@/assets/g_logo_violet.png'
import { useUser } from '@clerk/clerk-react'
import {
  generateActionItems,
  generateAnalysisSummary,
  buildExposureMap,
  loadCompletedActions,
  saveCompletedActions,
  EXPOSURE_COLUMNS,
  type ActionItem,
  type ActionPriority,
  type ExposureRow,
} from '@/lib/breachAnalysis'

export const Route = createFileRoute('/_auth/footprint')({
  component: RouteComponent,
})

// ── Helpers ──────────────────────────────────────────────────────────────────

function getLogoUrl(path: string): string {
  if (!path) return ''
  if (path.startsWith('http')) return path
  if (path.startsWith('/')) return `https://haveibeenpwned.com${path}`
  return path
}

function formatBreachDate(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const PRIORITY_DOT: Record<ActionPriority, string> = {
  urgent: 'bg-red-500',
  important: 'bg-amber-400',
  moderate: 'bg-slate-300',
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusVerdict({
  scanCompleted,
  breachCount,
  pendingCount,
  resolvedCount,
}: {
  scanCompleted: boolean
  breachCount: number
  pendingCount: number
  resolvedCount: number
}) {
  // Honesty first (TD-004): never imply "all clear" when the scan hasn't
  // actually completed. Then reassurance ("All clear") or a calm to-do.
  if (!scanCompleted && breachCount === 0) {
    return (
      <div className="rounded-card border border-slate-200/80 bg-white px-5 py-5 sm:px-6 shadow-card">
        <p className="text-lg font-semibold text-slate-900">Your first scan is still running</p>
        <p className="mt-1 text-base leading-relaxed text-slate-600">
          Checking your email against known breaches can take a little while.
          Your results will appear here, no need to wait around.
        </p>
      </div>
    )
  }
  if (breachCount === 0) {
    return (
      <div className="rounded-card bg-emerald-50 px-5 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600">
            <FiCheckCircle className="h-5 w-5" />
          </span>
          <div>
            <p className="text-lg font-semibold text-emerald-900">All clear</p>
            <p className="text-base leading-relaxed text-emerald-800">
              Your email hasn't appeared in any known breaches. We keep
              watching and will tell you if that changes.
            </p>
          </div>
        </div>
      </div>
    )
  }
  if (pendingCount === 0) {
    return (
      <div className="rounded-card bg-emerald-50 px-5 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600">
            <FiCheckCircle className="h-5 w-5" />
          </span>
          <div>
            <p className="text-lg font-semibold text-emerald-900">You're on top of it</p>
            <p className="text-base leading-relaxed text-emerald-800">
              Your email appeared in {breachCount} {breachCount === 1 ? 'breach' : 'breaches'}
              {resolvedCount > 0 ? `, and you've completed all ${resolvedCount} recommended ${resolvedCount === 1 ? 'action' : 'actions'}.` : ', and there is nothing you need to do right now.'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="rounded-card bg-violet-50 px-5 py-5 sm:px-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-violet-600">
          <FiShield className="h-5 w-5" />
        </span>
        <div>
          <p className="text-lg font-semibold text-violet-900">
            {pendingCount} {pendingCount === 1 ? 'thing' : 'things'} worth doing
          </p>
          <p className="text-base leading-relaxed text-violet-800">
            Your email appeared in {breachCount} known {breachCount === 1 ? 'breach' : 'breaches'}.
            That's common, and we've turned it into simple steps below.
          </p>
        </div>
      </div>
    </div>
  )
}

function ActionQueueItem({
  item,
  isCompleted,
  onToggleComplete,
}: {
  item: ActionItem
  isCompleted: boolean
  onToggleComplete: () => void
}) {
  const [isOpen, setIsOpen] = React.useState(false)

  if (isCompleted) {
    return (
      <div className="flex items-center gap-3 py-3 px-1 opacity-50">
        <FiCheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
        <p className="text-base text-slate-400 line-through flex-1">
          {item.title}
        </p>
        <button
          onClick={onToggleComplete}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors shrink-0"
        >
          Undo
        </button>
      </div>
    )
  }

  return (
    <div className="py-4 transition-colors hover:bg-slate-50/50 -mx-1 px-1 rounded-xl">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-start gap-3 text-left cursor-pointer"
      >
        <span
          className={`w-2 h-2 rounded-full shrink-0 mt-2 ${PRIORITY_DOT[item.priority]}`}
        />
        <div className="flex-1 min-w-0">
          <span className="text-base font-medium text-slate-900">
            {item.title}
          </span>
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            {item.reason}
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-slate-300 shrink-0 mt-1.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="pt-4 pl-5 ml-0.5">
          <div className="space-y-3 mb-5">
            {item.steps.map((step, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-sm font-medium text-slate-500 mt-0.5 shrink-0 w-4 text-right">
                  {i + 1}
                </span>
                <p className="text-base text-slate-700 leading-relaxed">
                  {step}
                </p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onToggleComplete}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
            >
              <FiCheckCircle className="w-3.5 h-3.5" />
              I've done this
            </button>
            <Link
              to="/contact-support"
              className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Ask for help
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

function ActionQueue({
  items,
  completedIds,
  onToggleComplete,
}: {
  items: ActionItem[]
  completedIds: Set<string>
  onToggleComplete: (id: string) => void
}) {
  const pending = items.filter((item) => !completedIds.has(item.id))
  const completed = items.filter((item) => completedIds.has(item.id))

  if (items.length === 0) {
    return (
      <div className="py-8 text-center">
        <FiShield className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
        <p className="text-base font-medium text-slate-700">
          No actions needed
        </p>
        <p className="text-sm text-slate-400 mt-1">
          We didn't find your email in any known breaches.
        </p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-slate-100">
      {pending.map((item) => (
        <ActionQueueItem
          key={item.id}
          item={item}
          isCompleted={false}
          onToggleComplete={() => onToggleComplete(item.id)}
        />
      ))}
      {completed.length > 0 && (
        <>
          <div className="pt-4 pb-1 px-1">
            <p className="text-sm font-medium text-slate-500">Completed</p>
          </div>
          {completed.map((item) => (
            <ActionQueueItem
              key={item.id}
              item={item}
              isCompleted={true}
              onToggleComplete={() => onToggleComplete(item.id)}
            />
          ))}
        </>
      )}
    </div>
  )
}

type SortKey = 'breach' | typeof EXPOSURE_COLUMNS[number]
type SortDir = 'asc' | 'desc'

function SortArrow({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <svg
      className={`inline-block w-3 h-3 ml-0.5 transition-colors ${active ? 'text-slate-600' : 'text-transparent group-hover:text-slate-300'}`}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      {dir === 'asc' ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 2.5v7M3 5.5l3-3 3 3" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 9.5v-7M3 6.5l3 3 3-3" />
      )}
    </svg>
  )
}

function ExposureMap({ rows }: { rows: ExposureRow[] }) {
  const [sortKey, setSortKey] = React.useState<SortKey>('breach')
  const [sortDir, setSortDir] = React.useState<SortDir>('asc')
  const [search, setSearch] = React.useState('')

  if (rows.length === 0) return null

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'breach' ? 'asc' : 'desc')
    }
  }

  const filtered = search
    ? rows.filter((r) =>
        r.breach.Title.toLowerCase().includes(search.toLowerCase()),
      )
    : rows

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    if (sortKey === 'breach') {
      return dir * a.breach.Title.localeCompare(b.breach.Title)
    }
    const col = sortKey as typeof EXPOSURE_COLUMNS[number]
    const aVal = a.cells[col] === 'exposed' ? 1 : 0
    const bVal = b.cells[col] === 'exposed' ? 1 : 0
    if (aVal !== bVal) return dir * (bVal - aVal)
    return a.breach.Title.localeCompare(b.breach.Title)
  })

  return (
    <div>
      <div className="relative mb-4 max-w-xs">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search breaches..."
          aria-label="Search breaches"
          className="w-full rounded-xl border border-slate-200/80 bg-white/80 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:border-slate-300 focus:ring-1 focus:ring-slate-200 transition-colors"
        />
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('breach')}
                className="group text-left text-xs font-medium text-slate-500 pb-4 pr-6 sticky left-0 bg-white/90 backdrop-blur-sm min-w-[180px] cursor-pointer select-none hover:text-slate-700 transition-colors"
              >
                Breach
                <SortArrow active={sortKey === 'breach'} dir={sortKey === 'breach' ? sortDir : 'asc'} />
              </th>
              {EXPOSURE_COLUMNS.map((col) => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  className="group text-center text-xs font-medium text-slate-500 pb-4 px-3 min-w-[80px] cursor-pointer select-none hover:text-slate-700 transition-colors"
                >
                  {col}
                  <SortArrow active={sortKey === col} dir={sortKey === col ? sortDir : 'desc'} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.breach.id}
                className="border-t border-slate-100/80"
              >
                <td className="py-5 pr-6 sticky left-0 bg-white/90 backdrop-blur-sm">
                  <div className="flex items-center gap-2.5">
                    {row.breach.LogoPath ? (
                      <img
                        src={getLogoUrl(row.breach.LogoPath)}
                        alt=""
                        className="w-7 h-7 rounded-lg object-contain bg-slate-50 border border-slate-200/80 p-0.5"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-lg bg-slate-100 border border-slate-200/60 flex items-center justify-center text-[10px] font-semibold text-slate-400">
                        {(row.breach.Title || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium text-slate-700 truncate max-w-[130px]">
                      {row.breach.Title}
                    </span>
                  </div>
                </td>
                {EXPOSURE_COLUMNS.map((col) => (
                  <td key={col} className="py-5 px-3 text-center">
                    {row.cells[col] === 'exposed' ? (
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 border border-red-200/60">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-8 h-8">
                        <span className="w-1 h-1 rounded-full bg-slate-200" />
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-slate-500 mt-5 leading-relaxed">
        Red squares show personal data exposed in that breach. Tap a column heading to sort.
      </p>
    </div>
  )
}

function BreachTimelineCard({ breach }: { breach: Breach }) {
  const [isOpen, setIsOpen] = React.useState(false)

  const typeBadgeClass =
    breach.Type === 'Accounts Compromised'
      ? 'bg-red-100 text-red-700'
      : breach.Type === 'Database Stolen'
        ? 'bg-violet-100 text-violet-700'
        : 'bg-slate-100 text-slate-600'

  const plainDesc = breach.Description.replace(/<[^>]*>/g, '')
  const isLong = plainDesc.length > 180
  const snippet = isLong ? plainDesc.slice(0, 180) + '...' : plainDesc

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/70 shadow-sm transition-all hover:border-slate-300/80 hover:shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 text-left cursor-pointer"
      >
        <div className="flex items-start gap-3">
          {breach.LogoPath ? (
            <img
              src={getLogoUrl(breach.LogoPath)}
              alt=""
              className="w-10 h-10 rounded-xl object-contain bg-slate-100 border border-slate-200 p-1 shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-500 shrink-0">
              {(breach.Title || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">
                {breach.Title}
              </span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadgeClass}`}
              >
                {breach.Type}
              </span>
            </div>
            <p className="text-base text-slate-600 mt-1 leading-relaxed">
              {isOpen ? plainDesc : snippet}
            </p>
          </div>
          <svg
            className={`w-4 h-4 text-slate-400 shrink-0 mt-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 pt-0 ml-[52px]">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {breach.DataClasses.map((dc) => (
              <span
                key={dc}
                className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
              >
                {dc}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>{breach.PwnCount.toLocaleString()} accounts affected</span>
            {breach.Domain && <span>{breach.Domain}</span>}
          </div>
          <Link
            to="/data-breaches/$breachId"
            params={{ breachId: breach.id }}
            className="inline-block mt-3 text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
          >
            View full details
          </Link>
        </div>
      )}
    </div>
  )
}

function BreachTimelineSection({ breaches }: { breaches: Breach[] }) {
  if (breaches.length === 0) return null

  const sorted = [...breaches].sort(
    (a, b) =>
      new Date(b.BreachDate).getTime() - new Date(a.BreachDate).getTime(),
  )

  return (
    <div className="relative">
      {/* Vertical connecting line */}
      <div className="absolute left-[15px] top-4 bottom-4 w-px bg-slate-200" />
      <div className="space-y-4">
        {sorted.map((breach) => (
          <div key={breach.id} className="relative pl-10">
            {/* Timeline dot */}
            <div className="absolute left-[10px] top-5 w-[11px] h-[11px] rounded-full border-2 border-slate-300 bg-white z-10" />
            {/* Date label */}
            <div className="text-[13px] font-medium text-slate-500 mb-1.5">
              {formatBreachDate(breach.BreachDate)}
            </div>
            <BreachTimelineCard breach={breach} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Report section definitions ───────────────────────────────────────────────

type ReportSectionId = 'analysis' | 'actions' | 'exposure' | 'history' | 'training' | 'faq'

const ALL_SECTIONS: { id: ReportSectionId; label: string; icon: LucideIcon }[] = [
  { id: 'analysis', label: 'Analysis', icon: Sparkles },
  { id: 'actions', label: 'Actions', icon: ListChecks },
  { id: 'exposure', label: 'Exposure Map', icon: Grid3x3 },
  { id: 'history', label: 'Breach History', icon: Clock },
  { id: 'training', label: 'Training', icon: BookOpen },
  { id: 'faq', label: 'FAQ', icon: HelpCircle },
]

// ── Main Component ───────────────────────────────────────────────────────────

function RouteComponent() {
  const { user: clerkUser } = useUser()
  const { data: ggUser, isLoading: isUserLoading, isError: isUserError } =
    getUserFromClerkId(clerkUser?.id ?? '', { enabled: !!clerkUser })
  const { data: breaches, isLoading, isError } = getBreaches()
  const { mutate: triggerBreaches, isPending: isTriggering } =
    useTriggerBreaches()
  const {
    data: articles,
    isLoading: isArticlesLoading,
    isError: isArticlesError,
  } = getArticles()

  // Explainer modal
  const [isExplainerOpen, setIsExplainerOpen] = React.useState(false)

  // Cooldown state for refresh button
  const [cooldown, setCooldown] = React.useState(0)
  React.useEffect(() => {
    let timer: number
    if (cooldown > 0) {
      timer = window.setTimeout(() => setCooldown(cooldown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [cooldown])

  // Completed actions (localStorage)
  const [completedIds, setCompletedIds] = React.useState<Set<string>>(
    () => loadCompletedActions(),
  )

  const handleToggleComplete = React.useCallback(
    (id: string) => {
      setCompletedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        saveCompletedActions(next)
        return next
      })
    },
    [],
  )

  // Derived data
  const actionItems = React.useMemo(
    () => generateActionItems(breaches ?? []),
    [breaches],
  )
  const exposureMap = React.useMemo(
    () => buildExposureMap(breaches ?? []),
    [breaches],
  )
  const analysisSummary = React.useMemo(
    () => generateAnalysisSummary(breaches ?? []),
    [breaches],
  )

  // Section tracking for sidebar TOC
  const [activeSection, setActiveSection] = React.useState<ReportSectionId>('analysis')
  const sectionRefs = React.useRef<Partial<Record<ReportSectionId, HTMLElement | null>>>({})

  const registerRef = (id: ReportSectionId) => (node: HTMLElement | null) => {
    sectionRefs.current[id] = node
  }

  const scrollToSection = (id: ReportSectionId) => {
    const target = sectionRefs.current[id]
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveSection(id)
    }
  }

  // Intersection observer for scroll tracking
  const breachCount = breaches?.length ?? 0
  const visibleSections = React.useMemo(() => {
    return ALL_SECTIONS.filter(s => {
      if (s.id === 'exposure' || s.id === 'history') return breachCount > 0
      return true
    })
  }, [breachCount])

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('data-section-id') as ReportSectionId | null
          if (id) setActiveSection(id)
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.2, 0.35, 0.6] },
    )

    visibleSections.forEach((s) => {
      const node = sectionRefs.current[s.id]
      if (node) observer.observe(node)
    })

    return () => observer.disconnect()
  }, [visibleSections, breaches])

  if (isUserLoading || isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-5 lg:px-6 pt-8 pb-16 sm:pt-10 animate-pulse">
          <div className="h-4 w-44 rounded bg-slate-100" />
          <div className="mt-6 h-9 w-72 rounded bg-slate-100" />
          <div className="mt-3 h-4 w-56 rounded bg-slate-100/70" />
          <div className="mt-8 h-24 w-full rounded-card bg-slate-100/60" />
          <div className="mt-6 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 w-full rounded-2xl bg-slate-100/50" />
            ))}
          </div>
        </div>
      </div>
    )
  }
  if (isUserError || isError) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="rounded-card border border-slate-200/80 bg-white px-8 py-12 text-center shadow-card">
          <p className="text-[17px] text-slate-700">
            We couldn't load your breach report just now.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex min-h-11 cursor-pointer items-center rounded-full bg-violet-600 px-6 py-2.5 text-base font-medium text-white transition-colors hover:bg-violet-700"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const pendingActions = actionItems.filter(
    (item) => !completedIds.has(item.id),
  )

  const handleRefresh = () => {
    triggerBreaches()
    setCooldown(60)
  }

  const prioritizedTrainingSlugs = [
    'two-factor-authentication-basics',
    'what-data-breaches-mean-for-you',
  ]

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-5 lg:px-6 pt-8 pb-16 sm:pt-10">

        {/* Page header */}
        <header className="mb-8">
          <Breadcrumbs
            items={[
              { label: 'Tools', href: '/tools' },
              { label: 'Breach Monitor' },
            ]}
          />
          <div className="flex items-center justify-between mt-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Data Breach Monitor
            </h1>
            <button
              onClick={() => setIsExplainerOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-slate-200/60 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 cursor-pointer"
            >
              <Sparkles className="h-4 w-4" />
              Explain this to me
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mt-2">
            <p className="text-base text-slate-500">
              {clerkUser?.primaryEmailAddress?.emailAddress ? (
                <>
                  Monitoring{' '}
                  <span className="font-medium text-slate-700">
                    {clerkUser.primaryEmailAddress.emailAddress}
                  </span>
                </>
              ) : (
                'Monitoring your email'
              )}
            </p>
            <button
              onClick={handleRefresh}
              disabled={isTriggering || cooldown > 0}
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 disabled:opacity-40 transition-colors cursor-pointer sm:ml-1.5 sm:before:content-['·'] sm:before:mr-1.5"
            >
              <FaSyncAlt className={`h-3 w-3 ${isTriggering ? 'animate-spin' : ''}`} />
              {isTriggering
                ? 'Scanning...'
                : cooldown > 0
                  ? `Rescan (${cooldown}s)`
                  : 'Rescan'}
            </button>
          </div>
        </header>

        {/* Report layout: sidebar + content */}
        <div className="grid items-start gap-6 md:grid-cols-[220px_minmax(0,1fr)]">

          {/* Sidebar TOC */}
          <aside className="hidden self-start md:sticky md:top-24 md:block md:border-r md:border-slate-200/60 md:pr-6">
            <nav aria-label="Report sections">
              <div className="space-y-0.5">
                {visibleSections.map((section) => {
                  const Icon = section.icon
                  const isActive = activeSection === section.id
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`group flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                        isActive
                          ? 'bg-slate-50 text-slate-900'
                          : 'text-slate-500 hover:bg-slate-50/60 hover:text-slate-900'
                      }`}
                    >
                      <Icon
                        className={`h-[18px] w-[18px] ${
                          isActive ? 'text-violet-600' : 'text-slate-400 group-hover:text-slate-600'
                        }`}
                      />
                      <span className="text-sm font-medium leading-none">
                        {section.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </nav>
          </aside>

          {/* Main report content */}
          <main>
            {/* Status summary */}
            <div className="mb-8">
              <StatusVerdict
                scanCompleted={!!ggUser?.dataBreachRetrievalDate}
                breachCount={breachCount}
                pendingCount={pendingActions.length}
                resolvedCount={completedIds.size}
              />
            </div>

            <div className="divide-y divide-slate-200/60">

            {/* Analysis */}
            <section
              ref={registerRef('analysis')}
              data-section-id="analysis"
              className="scroll-mt-28 py-8 first:pt-0 sm:py-10"
            >
              <div className="flex items-center gap-2.5 mb-4">
                <img src={gLogoViolet} alt="" className="w-6 h-6 object-contain" />
                <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                  GranGuard Analysis
                </h2>
              </div>

              <p className="text-base font-medium text-slate-800 mb-2">
                {analysisSummary.headline}
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                {analysisSummary.body}
              </p>

              {analysisSummary.keyFacts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {analysisSummary.keyFacts.map((fact) => (
                    <span
                      key={fact}
                      className="text-sm text-slate-500 bg-slate-50 px-2.5 py-1 rounded-lg"
                    >
                      {fact}
                    </span>
                  ))}
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <p className="text-sm font-medium text-slate-500 mb-2">
                  How this connects to scams
                </p>
                <p className="text-base text-slate-600 leading-relaxed">
                  {analysisSummary.scamConnection}
                </p>
              </div>
            </section>

            {/* Actions */}
            <section
              ref={registerRef('actions')}
              data-section-id="actions"
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-1">
                What you should do next
              </h2>
              <p className="text-base text-slate-500 mb-6">
                Tap any item for step-by-step guidance
              </p>
              <ActionQueue
                items={actionItems}
                completedIds={completedIds}
                onToggleComplete={handleToggleComplete}
              />
            </section>

            {/* Exposure Map */}
            {breachCount > 0 && (
              <section
                ref={registerRef('exposure')}
                data-section-id="exposure"
                className="scroll-mt-28 py-8 sm:py-10"
              >
                <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-1">
                  Where your data was exposed
                </h2>
                <p className="text-base text-slate-500 mb-6">
                  A breakdown of which personal data was included in each breach.
                </p>
                <ExposureMap rows={exposureMap} />
              </section>
            )}

            {/* Breach History */}
            {breachCount > 0 && (
              <section
                ref={registerRef('history')}
                data-section-id="history"
                className="scroll-mt-28 py-8 sm:py-10"
              >
                <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-6">
                  Breach history
                </h2>
                <BreachTimelineSection breaches={breaches ?? []} />
              </section>
            )}

            {/* Recommended Training */}
            <section
              ref={registerRef('training')}
              data-section-id="training"
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-2">
                Recommended training
              </h2>
              <p className="text-base text-slate-500 mb-6">
                Complete these to strengthen your defences.
              </p>
              {isArticlesLoading ? (
                <div className="py-8 text-center text-slate-500">
                  Loading articles...
                </div>
              ) : isArticlesError ? (
                <div className="py-8 text-center text-red-500">
                  Error loading articles.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {articles &&
                    [...articles]
                      .filter((a) => !a.archived)
                      .sort((a, b) => {
                        const slugA: string = a.slug ?? ''
                        const slugB: string = b.slug ?? ''
                        const ia =
                          slugA.length > 0
                            ? prioritizedTrainingSlugs.indexOf(slugA)
                            : -1
                        const ib =
                          slugB.length > 0
                            ? prioritizedTrainingSlugs.indexOf(slugB)
                            : -1
                        const aIsPrioritized = ia !== -1
                        const bIsPrioritized = ib !== -1
                        if (aIsPrioritized && bIsPrioritized) return ia - ib
                        if (aIsPrioritized) return -1
                        if (bIsPrioritized) return 1
                        return (b.importance ?? 0) - (a.importance ?? 0)
                      })
                      .slice(0, 3)
                      .map((article) => (
                        <ArticleCard
                          key={article.id}
                          title={article.title}
                          description={article.description}
                          thumbnail={article.thumbnail}
                          link={{ to: `/articles/${article.slug}` }}
                          tags={article.tags || []}
                          articleType={article.articleType}
                          readTime={article.readTime}
                          updatedAt={article.publishedAt || article.updatedAt}
                        />
                      ))}
                </div>
              )}
            </section>

            {/* FAQ */}
            <section
              ref={registerRef('faq')}
              data-section-id="faq"
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-4">
                Frequently asked questions
              </h2>
              <div className="space-y-2">
                <CollapsibleSection title="What is a data breach?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    A data breach happens when information held by a company or website is exposed or stolen, often through a hack, a misconfiguration, or an accidental leak. Breaches can include emails, passwords, addresses, and more.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="Does a breach mean my accounts are hacked?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    Not necessarily. Breaches expose information that attackers can use in future scams or login attempts. If a password was leaked, change it anywhere you reused it and turn on two-factor authentication (2FA).
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="How did you find my exposure?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    We check trusted breach sources and your authorized account details, then match known exposures to your email. We don't guess passwords or scan your inbox.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="What should I do first?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    Start with 2FA on your email and bank, change any reused passwords, and be cautious with unexpected messages. Our recommended training will walk you through the essentials.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="What is credential stuffing?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    Attackers try leaked email/password pairs on many sites to take over accounts. Using unique passwords and 2FA blocks most of these attempts.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="What is two-factor authentication (2FA)?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    2FA adds a second step (like a code or app approval) after your password. Even if your password leaks, 2FA can stop account takeovers. See our training above to get started.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="How do you protect my privacy?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    We only use your data to check for known breaches and to guide your security steps. We don't sell your information. You can remove your data at any time in account settings.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="Why do I see my info here if I never used those sites?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    Your data can be shared or stored by partners, old accounts, or companies you interacted with indirectly. Breaches often include large datasets collected over time.
                  </p>
                </CollapsibleSection>
                <CollapsibleSection title="How often should I check for breaches?" className="bg-slate-50/50 border border-slate-200/60">
                  <p className="text-slate-700 text-base">
                    New breaches are reported regularly. Tap Rescan above to check again. Monthly is a good rhythm, or sooner if you hear about a major incident.
                  </p>
                </CollapsibleSection>
              </div>
            </section>

            </div>
          </main>
        </div>
      </div>

      {/* Breach Explainer modal */}
      <BreachExplainer
        isOpen={isExplainerOpen}
        onClose={() => setIsExplainerOpen(false)}
        breaches={breaches ?? []}
        actionItems={actionItems}
        userEmail={clerkUser?.primaryEmailAddress?.emailAddress}
      />
    </div>
  )
}
